import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Spin, Typography, Card, Row, Col, message, Button } from 'antd';
import { 
  PieChartOutlined,
  BarChartOutlined,
  KeyOutlined,
  UserOutlined,
  AreaChartOutlined,
  AppstoreOutlined,
  SearchOutlined,
  ClearOutlined,
  ApiOutlined,
  FundOutlined,
  FireOutlined,
  BellOutlined,
  DownloadOutlined,
  LineChartOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import { ApiService, clearCache } from '../services/api';
import DateRangePicker from './DateRangePicker';
import UsageAnalysis from './UsageAnalysis';
import KeywordAnalysis from './KeywordAnalysis';
import ApiQsearchAnalysis from './ApiQsearchAnalysis';
import TrendRequestAnalysis from './TrendRequestAnalysis';
import AlertAnalysis from './AlertAnalysis';
import ExportAnalysis from './ExportAnalysis';
import ApiDailyAnalysis from './ApiDailyAnalysis';
import AdsApiAnalysis from './AdsApiAnalysis';
import OrgFilter from './OrgFilter';
import { filterDataByOrgs } from '../utils/dataFilter';
import { isUnauthorizedError } from '../utils/auth';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { todayTpe, daysAgoTpe } from '../utils/datetime';

interface DashboardProps {
  defaultMenu?: string;
}

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

// 菜單項定義
const menuItems = [
  {
    key: 'usage',
    icon: <BarChartOutlined />,
    label: 'Analytics 使用分析',
    children: [
      {
        key: 'engagement_score',
        icon: <UserOutlined />,
        label: '使用情況概覽',
      },
      {
        key: 'resource_usage',
        icon: <AppstoreOutlined />,
        label: '資源使用情況',
      },
      {
        key: 'amount_daily',
        icon: <AreaChartOutlined />,
        label: '每日搜尋次數',
      },
      {
        key: 'keyword_count',
        icon: <KeyOutlined />,
        label: '關鍵詞數量',
      },
      {
        key: 'term_org_count',
        icon: <PieChartOutlined />,
        label: '關鍵詞跨組織使用',
      },
      {
        key: 'most_frequent_keyword',
        icon: <SearchOutlined />,
        label: '常用關鍵詞',
      },
      {
        key: 'repeat_keyword',
        icon: <SearchOutlined />,
        label: '重複關鍵詞分析',
      },
    ],
  },
  {
    key: 'api_qsearch',
    icon: <ApiOutlined />,
    label: 'API QSearch 客戶分析',
    children: [
      {
        key: 'api_qsearch_overview',
        icon: <ApiOutlined />,
        label: 'API 客戶概覽',
      },
      {
        key: 'api_qsearch_daily',
        icon: <LineChartOutlined />,
        label: '每日呼叫趨勢',
      },
      {
        key: 'api_qsearch_endpoints',
        icon: <AppstoreOutlined />,
        label: '服務與 Endpoint',
      },
      {
        key: 'api_qsearch_ads',
        icon: <NotificationOutlined />,
        label: '廣告監測 API',
      },
    ],
  },
  {
    key: 'trend_requests',
    icon: <FundOutlined />,
    label: 'Trend 產品行為分析',
    children: [
      {
        key: 'trend_hotposts_summary',
        icon: <FireOutlined />,
        label: '熱門文章摘要',
      },
      {
        key: 'trend_alert',
        icon: <BellOutlined />,
        label: 'Alert 分析',
      },
      {
        key: 'trend_export',
        icon: <DownloadOutlined />,
        label: '文章匯出分析',
      },
    ],
  },
];

// 路由映射對象
const routeMap: Record<string, string> = {
  'engagement_score': '/usage/engagement-score',
  'resource_usage': '/usage/resource-usage',
  'amount_daily': '/usage/daily-search',
  'keyword_count': '/analytics/keywords/count',
  'term_org_count': '/analytics/keywords/org-usage',
  'most_frequent_keyword': '/analytics/keywords/search-records',
  'repeat_keyword': '/analytics/keywords/repeat-analysis',
  'api_qsearch_overview': '/api-qsearch/overview',
  'api_qsearch_daily': '/api-qsearch/daily',
  'api_qsearch_endpoints': '/api-qsearch/endpoints',
  'api_qsearch_ads': '/api-qsearch/ads',
  'trend_hotposts_summary': '/trend-requests/hotposts-summary',
  'trend_alert': '/trend-requests/alert',
  'trend_export': '/trend-requests/export',
};

// 反向路由映射
const pathToMenuMap: Record<string, string> = Object.fromEntries(
  Object.entries(routeMap).map(([menu, path]) => [path, menu])
);

const analyticsKeywordMenus = new Set([
  'keyword_count',
  'term_org_count',
  'most_frequent_keyword',
  'repeat_keyword',
]);

// 這些頁面本身就以客戶為列（或資料沒有組織欄位），不需要外掛組織篩選器
const noOrgFilterMenus = new Set([
  'amount_daily',
  'trend_features',
  'trend_alert',
  'trend_export',
  'api_qsearch_daily',
  'api_qsearch_ads',
]);

const Dashboard: React.FC<DashboardProps> = ({ defaultMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // 根據當前路徑或 defaultMenu 設置初始菜單
  const getInitialMenu = () => {
    if (defaultMenu) return defaultMenu;
    return pathToMenuMap[location.pathname] || 'engagement_score';
  };
  
  // 從 URL 參數獲取時間範圍，如果沒有則使用默認值
  const getInitialDateRange = (): [string, string] | null => {
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    
    if (startDate && endDate) {
      return [startDate, endDate];
    }
    
    // 默認時間範圍：最近30天（以台灣時區今天為基準）
    return [daysAgoTpe(30), todayTpe()];
  };
  
  const [selectedMenu, setSelectedMenu] = useState(getInitialMenu());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[string, string] | null>(getInitialDateRange());

  // 更新 URL 參數
  // newDateRange 語意：undefined = 沿用現值；null = 使用者清空日期（要從 URL 移除）
  const updateUrlParams = (newDateRange?: [string, string] | null, newMenu?: string) => {
    const currentDateRange = newDateRange === undefined ? dateRange : newDateRange;
    const currentMenu = newMenu || selectedMenu;
    const targetPath = routeMap[currentMenu];
    
    if (targetPath) {
      const params = new URLSearchParams();
      if (currentDateRange) {
        params.set('start_date', currentDateRange[0]);
        params.set('end_date', currentDateRange[1]);
      }
      
      const newUrl = `${targetPath}?${params.toString()}`;
      navigate(newUrl, { replace: true });
    }
  };

  // 處理菜單選擇
  const handleMenuSelect = (key: string) => {
    setSelectedMenu(key);
    updateUrlParams(dateRange, key);
  };

  // 處理時間範圍變更
  const handleDateRangeChange = (newDateRange: [string, string] | null) => {
    setDateRange(newDateRange);
    updateUrlParams(newDateRange, selectedMenu);
  };

  // 初始化 URL 參數
  useEffect(() => {
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    
    // 如果 URL 中沒有時間參數，則設置默認值
    if (!startDate || !endDate) {
      updateUrlParams(dateRange, selectedMenu);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 監聽 URL 參數變化，同步狀態
  useEffect(() => {
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const currentMenu = pathToMenuMap[location.pathname] || 'engagement_score';
    
    // 同步菜單狀態
    if (currentMenu !== selectedMenu) {
      setSelectedMenu(currentMenu);
    }
    
    // 同步時間範圍狀態
    if (startDate && endDate) {
      const urlDateRange: [string, string] = [startDate, endDate];
      if (!dateRange || dateRange[0] !== startDate || dateRange[1] !== endDate) {
        setDateRange(urlDateRange);
      }
    }
  }, [location.pathname, searchParams, selectedMenu, dateRange]);

  useEffect(() => {
    fetchData();
    // fetchData 依 selectedMenu 與 dateRange 重新查詢；函式本身不作為 dependency。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu, dateRange]);

  // 遞增序號防止 race condition：快速切換選單/日期時，
  // 較慢的舊回應不可覆蓋較新的回應（型別也可能與當前頁面不符）
  const fetchSeqRef = useRef(0);

  const fetchData = async () => {
    const seq = ++fetchSeqRef.current;
    const isCurrent = () => seq === fetchSeqRef.current;
    setLoading(true);
    try {
      const params = dateRange ? {
        start_date: dateRange[0],
        end_date: dateRange[1] || undefined
      } : undefined;

      let responseData;
      switch (selectedMenu) {
        case 'resource_usage':
          responseData = await ApiService.getResourceUsage(params);
          break;
        case 'amount_daily':
          // amount_daily 不在這裡調用API，由 UsageAnalysis 組件內部處理
          // 只傳遞時間範圍信息
          responseData = {
            time_range: {
              start_date: params?.start_date,
              end_date: params?.end_date
            },
            data: [] // 空數據，實際數據由組件內部獲取
          };
          break;
        case 'keyword_count':
          responseData = await ApiService.getKeywordCount(params);
          break;
        case 'term_org_count':
          responseData = await ApiService.getTermOrgCount(params);
          break;
        case 'most_frequent_keyword':
          responseData = await ApiService.getMostFrequentKeyword(params);
          break;
        case 'repeat_keyword':
          responseData = await ApiService.getRepeatKeyword(params);
          break;
        case 'engagement_score':
          responseData = await ApiService.getEngagementScore(params);
          break;
        case 'api_qsearch_overview':
          responseData = await ApiService.getApiQsearchOverview(params);
          break;
        case 'api_qsearch_daily':
          responseData = await ApiService.getApiQsearchDaily(params);
          break;
        case 'api_qsearch_endpoints':
          responseData = await ApiService.getApiQsearchEndpoints(params);
          break;
        case 'api_qsearch_ads':
          responseData = await ApiService.getAdsUsage(params);
          break;
        case 'trend_hotposts_summary':
          responseData = await ApiService.getHotpostsSummaryUsage(params);
          break;
        case 'trend_alert':
          responseData = await ApiService.getTrendAlertConfig(params);
          break;
        case 'trend_export':
          responseData = await ApiService.getTrendExportUsage(params);
          break;
        default:
          responseData = null;
      }
      if (isCurrent()) {
        setData(responseData);
      }
    } catch (error: any) {
      if (!isCurrent()) return;
      // 如果是 401 錯誤，用戶已經被重定向到登入頁面
      // 不需要顯示錯誤訊息
      if (isUnauthorizedError(error)) {
        return;
      }

      message.error('獲取數據失敗，請稍後再試');
      console.error('獲取數據失敗:', error);
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  };

  // 根據選中的菜單項確定要顯示的標題
  const getPageTitle = () => {
    for (const group of menuItems) {
      if (group.children) {
        const found = group.children.find(item => item.key === selectedMenu);
        if (found) return found.label;
      } else if (group.key === selectedMenu) {
        return group.label;
      }
    }
    return '儀表板';
  };

  // 根據選中的菜單項確定菜單分組
  const getMenuGroup = () => {
    for (const group of menuItems) {
      if (group.children) {
        const found = group.children.find(item => item.key === selectedMenu);
        if (found) return group.key;
      }
    }
    return null;
  };

  const menuGroup = getMenuGroup();

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (!data) {
      return (
        <Card>
          <Typography.Text>無可用數據</Typography.Text>
        </Card>
      );
    }

    // 應用組織篩選
    const filteredData = filterDataByOrgs(data, selectedOrgs);

    return (
      <>
        {/* 這幾頁自帶客戶維度（或無組織欄位），不套用組織篩選器 */}
        {!noOrgFilterMenus.has(selectedMenu) && (
          <OrgFilter 
            data={data} 
            selectedOrgs={selectedOrgs} 
            onFilterChange={setSelectedOrgs} 
          />
        )}
        {/* 根據菜單分組選擇要顯示的組件 */}
        {(() => {
          if (analyticsKeywordMenus.has(selectedMenu)) {
            return <KeywordAnalysis data={filteredData} type={selectedMenu} />;
          }
          if (selectedMenu === 'trend_alert') {
            return <AlertAnalysis data={filteredData} />;
          }
          if (selectedMenu === 'trend_export') {
            return <ExportAnalysis data={filteredData} />;
          }
          if (selectedMenu === 'api_qsearch_daily') {
            return <ApiDailyAnalysis data={filteredData} />;
          }
          if (selectedMenu === 'api_qsearch_ads') {
            return <AdsApiAnalysis data={filteredData} />;
          }
          switch (menuGroup) {
            case 'usage':
              // amount_daily 使用原始數據，其他使用篩選後的數據
              return <UsageAnalysis 
                data={selectedMenu === 'amount_daily' ? data : filteredData} 
                type={selectedMenu} 
              />;
            case 'api_qsearch':
              return <ApiQsearchAnalysis data={filteredData} type={selectedMenu} />;
            case 'trend_requests':
              return <TrendRequestAnalysis data={filteredData} type={selectedMenu} />;
            default:
              return <div>請選擇一個選項</div>;
          }
        })()}
      </>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#001529', padding: '0 20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ color: 'white', margin: '16px 0' }}>客戶使用分析儀表板</Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<ClearOutlined />} 
              onClick={() => {
                clearCache();
                message.success('快取已清除');
                fetchData(); // 重新載入當前數據
              }}
              style={{ marginRight: '20px' }}
            >
              清除快取
            </Button>
          </Col>
        </Row>
      </Header>
      <Layout>
        <Sider width={200} style={{ background: '#fff' }}>
          <Menu
            mode="inline"
            selectedKeys={[selectedMenu]}
            defaultOpenKeys={['usage', 'api_qsearch', 'trend_requests']}
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
            onSelect={({ key }) => handleMenuSelect(key as string)}
          />
        </Sider>
        <Layout style={{ padding: '0 24px 24px' }}>
          <Card style={{ marginTop: 16, marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>{getPageTitle()}</Title>
              </Col>
              <Col>
                <DateRangePicker 
                  value={dateRange} 
                  onChange={handleDateRangeChange} 
                />
              </Col>
            </Row>
          </Card>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: '#fff',
            }}
          >
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
